// next-app/src/hooks/use-respondents.ts
'use client';

import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { useAuthStore } from '@/store/auth-store';
import { queryKeys } from './query-keys';
import type { RespondentsResponse } from '@/types';

// ============================================================================
// Send 2FA Email Mutation
// ============================================================================

interface UseSend2FAOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to send 2FA verification code via email.
 *
 * Original: POST v1/event/send-2fa-email/{email}
 *
 * Code is valid for same-day access to respondents list.
 */
export function useSend2FA(options: UseSend2FAOptions = {}) {
  const setTwoFACode = useAuthStore((state) => state.setTwoFACode);

  return useMutation({
    mutationFn: async (contactEmail: string) => {
      const { response, result } = await api.send2FAEmail(contactEmail);

      if (!response.ok) {
        throw new Error('Failed to send verification code');
      }

      return result as { code?: string };
    },
    onSuccess: (data) => {
      // In development, the API may return the code for testing
      if (data.code) {
        console.log('[useSend2FA] Dev code received');
        setTwoFACode(data.code);
      }

      options.onSuccess?.();
    },
    onError: (error: Error) => {
      console.error('[useSend2FA] Error:', error.message);
      options.onError?.(error);
    },
  });
}

// ============================================================================
// Get Respondents Query
// ============================================================================

interface UseRespondentsOptions {
  enabled?: boolean;
}

/**
 * Hook to fetch survey respondents for an event.
 *
 * Original: GET v1/event/list-respondents/{id}
 *
 * Requires valid 2FA verification (same-day code).
 * Typically called after 2FA verification succeeds.
 */
export function useRespondents(
  eventId: string | number | undefined,
  options: UseRespondentsOptions = {}
) {
  const token = useAuthStore((state) => state.dealerToken);

  return useQuery({
    queryKey: queryKeys.respondents.byEvent(eventId ?? ''),
    queryFn: async () => {
      if (!eventId) {
        throw new Error('Event ID is required');
      }

      const { response, result } = await api.getRespondents(String(eventId));

      if (!response.ok) {
        throw new Error('Failed to fetch respondents. Verification may be required.');
      }

      return result as RespondentsResponse;
    },
    enabled: options.enabled !== false && Boolean(token) && Boolean(eventId),
    // Don't cache too long - respondents list is sensitive
    staleTime: 2 * 60 * 1000, // 2 minutes
    // Don't retry on error - likely a verification issue
    retry: false,
  });
}

/**
 * Selector hook to get respondents array.
 */
export function useRespondentsList(eventId: string | number | undefined) {
  const { data, ...rest } = useRespondents(eventId);
  return {
    ...rest,
    respondents: data?.respondents ?? [],
  };
}
